import { Grid, Checkbox, FormControlLabel, FormControl, Radio, RadioGroup } from '@mui/material';
import { memo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import MunicipalityRelease from './municipality-release';
import ConsulateRelease from './consulate-release';
import { FormikErrors, FormikTouched } from 'formik';
import TypeIndentityCard from 'shell/components/shared/type-indentity-card';
import { AccordionIdentityCard as AccordionIdentityCardVM } from 'shell/components/shared/sipo-person-form/new-person';

interface IdentityCardProps {
  disabled?: boolean;
  values?: AccordionIdentityCardVM;
  onChange: (
    key: keyof AccordionIdentityCardVM,
    value: AccordionIdentityCardVM[keyof AccordionIdentityCardVM]
  ) => void;
  onChangeValues: (values: AccordionIdentityCardVM) => void;
  isEditMode?: boolean;
  errors?: FormikErrors<AccordionIdentityCardVM>;
  touched?: FormikTouched<AccordionIdentityCardVM>;
}

const IdentityCardFormComponent = ({
  disabled,
  onChange,
  onChangeValues,
  values,
  isEditMode = false,
  errors,
  touched,
}: IdentityCardProps): React.ReactElement => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Numero *"
          aria-label="Numero"
          id="identity-card-number"
          fullWidth
          onChange={(event) => {
            onChange('number', event?.target.value);
          }}
          inputProps={{
            maxLength: '20',
          }}
          value={values?.number || ''}
          disabled={isEditMode ? false : disabled}
          error={touched?.number && Boolean(errors?.number)}
          helperText={errors?.number}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TypeIndentityCard
          label="Tipo"
          value={values?.type || ''}
          onChange={(type) => {
            onChange('type', type);
          }}
          disabled={isEditMode ? false : disabled}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Checkbox
              id="expatriationBan"
              checked={values?.expatriationBan || false}
              onChange={() => onChange('expatriationBan', !values?.expatriationBan)}
              inputProps={{ 'aria-label': 'Interdizione espatrio' }}
              disabled={isEditMode ? false : disabled}
            />
          }
          label="Interdizione espatrio"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data rilascio *"
          value={values?.releaseDate || null}
          disabled={isEditMode ? false : disabled}
          onChange={(dataValue) => {
            onChange('releaseDate', dataValue || null);
          }}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="releaseDate"
              fullWidth
              helperText={errors?.releaseDate}
              error={touched?.releaseDate && Boolean(errors?.releaseDate)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data scadenza"
          value={values?.expirationDate || null}
          disabled={isEditMode ? false : disabled}
          onChange={(dataValue) => {
            onChange('expirationDate', dataValue || null);
          }}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="expirationDate"
              fullWidth
              helperText={errors?.expirationDate}
              error={touched?.expirationDate && Boolean(errors?.expirationDate)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data annullamento"
          value={values?.cancellationDate || null}
          disabled={isEditMode ? false : disabled}
          onChange={(dataValue) => {
            onChange('cancellationDate', dataValue || null);
          }}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="cancellationDate"
              fullWidth
              helperText={errors?.cancellationDate}
              error={touched?.cancellationDate && Boolean(errors?.cancellationDate)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset" disabled={isEditMode ? false : disabled}>
          <RadioGroup
            row
            aria-label="Rilascio"
            id="isConsulateRelease"
            value={false}
            onChange={() => {
              onChangeValues({
                ...values,
                isConsulateRelease: !values?.isConsulateRelease,
                municipality: null,
                location: '',
                consulateCode: '',
              });
              return;
            }}
          >
            <FormControlLabel
              value={values?.isConsulateRelease}
              control={<Radio />}
              label="Comune rilascio"
            />
            <FormControlLabel
              value={!values?.isConsulateRelease}
              control={<Radio />}
              label="Consolato rilascio"
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {values?.isConsulateRelease ? (
        <ConsulateRelease
          disabled={disabled}
          values={values}
          onChange={onChange}
          isEditMode={isEditMode}
          errors={errors}
          touched={touched}
        />
      ) : (
        <MunicipalityRelease
          disabled={disabled}
          values={values}
          onChange={onChange}
          isEditMode={isEditMode}
          errors={errors}
          touched={touched}
        />
      )}
    </Grid>
  );
};

IdentityCardFormComponent.displayName = 'Form Identity Card component';

const IdentityCardForm = memo(IdentityCardFormComponent);

export default IdentityCardForm;
